import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full h-13 py-4 px-5 rounded-2xl bg-[#ffffff] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors";

export const ForgotOrderNumber = ({ initialEmail = "" }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const send = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await axios.post(`${API}/orders/remind`, { email: email.trim(), origin_url: window.location.origin });
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not send. Try again.");
      setState("idle");
    }
  };

  return (
    <div data-testid="forgot-order-number" className="bg-[#faf9f6]/60 border border-[#e3e2dc] rounded-3xl p-6 sm:p-8">
      <button type="button" data-testid="forgot-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 text-left">
        <span>
          <span className="font-display font-extrabold uppercase text-base block">Forgot your order number?</span>
          <span className="text-[#5f5e5a] text-sm mt-1 block">We'll email every order number linked to your address.</span>
        </span>
        <span className={`shrink-0 h-9 w-9 rounded-full border border-[#e3e2dc] flex items-center justify-center text-[#0a0a0b] transition-transform duration-300 ${open ? "rotate-45" : ""}`}>
          <span className="text-lg leading-none">+</span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            {state === "done" ? (
              <p data-testid="forgot-success" className="mt-6 text-sm text-[#0a0a0b] bg-[#ffffff] border border-[#e3e2dc] rounded-2xl p-4 flex items-start gap-3">
                <Mail size={16} className="text-[#ff2a85] mt-0.5 shrink-0" />
                If we have orders for <strong className="mx-1">{email}</strong>, we've sent them. Check your inbox (and spam) in a minute.
              </p>
            ) : (
              <form data-testid="forgot-form" onSubmit={send} className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3">
                <input data-testid="forgot-email" className={input} required type="email" placeholder="Email used at checkout" value={email} onChange={(e) => setEmail(e.target.value)} />
                <button data-testid="forgot-submit" type="submit" disabled={state === "sending"} className="h-13 px-6 rounded-2xl bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
                  {state === "sending" ? <Loader2 size={14} className="animate-spin" /> : "Email me"}
                </button>
                {error && <p data-testid="forgot-error" className="sm:col-span-2 text-xs text-[#ff2a85] font-mono-d uppercase tracking-wider">{error}</p>}
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
